import React, { useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash, User, Phone } from "@phosphor-icons/react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/lorryApi";

export default function DriversSheet({ open, onOpenChange, drivers = [], onChanged }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const addDriver = async (e) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) {
      toast.error("Driver name required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/drivers", { name: n, phone: phone.trim() });
      toast.success(`${n} added to directory`);
      setName("");
      setPhone("");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save driver");
    } finally {
      setSaving(false);
    }
  };

  const removeDriver = async (d) => {
    if (!d.id) {
      toast.error("Driver has trips — remove those first, or leave in directory");
      return;
    }
    setBusyId(d.id);
    try {
      await api.delete(`/drivers/${d.id}`);
      toast.success(`${d.name} removed`);
      onChanged?.();
    } catch (e) {
      toast.error("Failed to remove");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="drivers-sheet"
        side="right"
        className="bg-zinc-950 border-l border-zinc-800 rounded-none w-full sm:max-w-[420px] p-0 overflow-y-auto"
      >
        <div className="p-6 border-b border-zinc-800">
          <SheetHeader className="text-left">
            <span className="overline text-yellow-500">Directory</span>
            <SheetTitle className="font-display text-2xl font-black tracking-tight text-zinc-100">
              Drivers
            </SheetTitle>
            <SheetDescription className="text-zinc-500 text-sm">
              Store driver names once and pick them from suggestions when logging loads.
            </SheetDescription>
          </SheetHeader>
        </div>

        <form onSubmit={addDriver} className="p-6 border-b border-zinc-800 space-y-3">
          <div>
            <Label className="overline text-zinc-500">Name</Label>
            <Input
              data-testid="driver-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Rajesh Kumar"
              className="mt-1.5 bg-zinc-900 border-zinc-800 rounded-sm focus-visible:ring-yellow-500"
            />
          </div>
          <div>
            <Label className="overline text-zinc-500">Phone (optional)</Label>
            <Input
              data-testid="driver-phone-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 98765 43210"
              inputMode="tel"
              className="mt-1.5 bg-zinc-900 border-zinc-800 rounded-sm focus-visible:ring-yellow-500"
            />
          </div>
          <Button
            type="submit"
            data-testid="driver-save-btn"
            disabled={saving}
            className="w-full rounded-sm bg-yellow-500 hover:bg-yellow-400 text-zinc-950 font-semibold tracking-wide active:scale-[0.98] transition-transform"
          >
            <UserPlus size={16} weight="bold" className="mr-1.5" />
            {saving ? "Saving..." : "Add Driver"}
          </Button>
        </form>

        <div className="p-6 space-y-2">
          <div className="overline text-zinc-500 mb-3">
            {drivers.length} driver{drivers.length === 1 ? "" : "s"} on record
          </div>

          {drivers.length === 0 && (
            <div className="p-6 text-center border border-dashed border-zinc-800 bg-zinc-900/30">
              <p className="text-zinc-400 text-sm">
                No drivers yet. Add one above or register a lorry — the driver will be saved automatically.
              </p>
            </div>
          )}

          {drivers.map((d) => (
            <div
              key={d.name}
              data-testid={`driver-row-${d.name}`}
              className="border border-zinc-800 bg-zinc-900/40 p-3 flex items-center gap-3 hover:bg-zinc-900/70 transition-colors"
            >
              <div className="w-9 h-9 bg-zinc-800 border border-zinc-700 flex items-center justify-center flex-shrink-0">
                <User size={16} weight="bold" className="text-yellow-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-zinc-100 font-semibold text-sm truncate">{d.name}</div>
                <div className="flex items-center gap-2 mt-0.5 text-xs text-zinc-500">
                  {d.phone ? (
                    <span className="inline-flex items-center gap-1">
                      <Phone size={11} weight="bold" /> {d.phone}
                    </span>
                  ) : null}
                  <span>
                    {d.trips} trip{d.trips === 1 ? "" : "s"}
                  </span>
                  {d.saved && (
                    <span className="text-yellow-500/80 uppercase tracking-widest text-[0.6rem]">
                      Saved
                    </span>
                  )}
                </div>
              </div>
              {d.id && (
                <button
                  data-testid={`driver-delete-${d.name}`}
                  onClick={() => removeDriver(d)}
                  disabled={busyId === d.id}
                  className="p-2 text-zinc-500 hover:text-red-500 hover:bg-zinc-800 transition-colors rounded-sm"
                  title="Remove from directory"
                >
                  <Trash size={14} weight="bold" />
                </button>
              )}
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
